import {Component, OnInit, OnDestroy, inject, output, input, effect} from '@angular/core';
import {
    FormArray,
    UntypedFormBuilder,
    UntypedFormGroup,
    Validators,
    FormsModule,
    ReactiveFormsModule
} from "@angular/forms";
import {ProjectStatus, ViewMode, Right, TeamRole, strategicClassifications} from "../../../interfaces";
import {DataService} from "../../../../services/data.service";
import {FormService} from "../../../../services/form.service";
import Swal from "sweetalert2";
import {ProjectStatusPipe} from "../../../../pipes/project-status.pipe";
import {AccessService} from "../../../../services/access.service";
import {ToastrService} from "ngx-toastr";
import {ProjectService} from "../../../../services/project.service";
import {NgIf, NgFor} from '@angular/common';
import {TooltipModule} from 'ngx-bootstrap/tooltip';
import {DisableControlDirective} from '../../../../directives/disable-control.directive';
import {YesNoPipe} from '../../../../pipes/yes-no.pipe';
import {Subscription} from "rxjs";

@Component({
    selector: 'app-project-goals',
    templateUrl: './project-goals.component.html',
    styleUrl: './project-goals.component.scss',
    imports: [NgIf, TooltipModule, FormsModule, ReactiveFormsModule, DisableControlDirective, NgFor, YesNoPipe]
})
export class ProjectGoalsComponent implements OnInit, OnDestroy {

    accessService = inject(AccessService);
    formService = inject(FormService);
    projectService = inject(ProjectService);
    private dataService = inject(DataService);
    private fb = inject(UntypedFormBuilder);
    private projectStatusPipe = inject(ProjectStatusPipe);
    private toastrService = inject(ToastrService);

    readonly strategicClassifications = strategicClassifications;
    readonly ViewMode = ViewMode;
    readonly Right = Right;
    readonly ProjectStatus = ProjectStatus;
    readonly TeamRole = TeamRole;

    readonly PID = input.required<any>();
    readonly reloadTrigger = input<number>(0);

    readonly changeProject = output();
    readonly changeViewMode = output<boolean>();

    viewMode!: ViewMode;
    project: any;
    form!: UntypedFormGroup;

    private strategicClassificationChanges$: Subscription;

    private reloadEffect = effect(() => {
        if (this.reloadTrigger() > 0) {
            this.getProject();
        }
    })

    ngOnInit(): void {
        this.viewMode = ViewMode.VIEW;

        this.form = this.fb.group({
            id: [null],
            title_project: [null],
            number: [null],
            strategic_classification_root: [''],
            strategic_classification: [''],
            goals: this.fb.array([]),
            non_goals: this.fb.array([]),
            criteria: this.fb.array([]),
        });

        if (this.PID() !== '0') {
            this.getProject();
        }

        this.strategicClassificationChanges$ = this.form.get('strategic_classification_root').valueChanges.subscribe(val => {
            this.formService.addValidators(this.form, ['strategic_classification'], [Validators.required]);
        });
    }

    ngOnDestroy(): void {
        this.strategicClassificationChanges$?.unsubscribe();
    }

    minLengthArray(min: number) {
        return (control: FormArray): { [key: string]: any } => {

            const length = control.controls.filter(control => {
                return control.value._deleted == false;
            }).length;
            if (length >= min)
                return null;

            return {MinLengthArray: true};
        }
    }

    getProject(): void {
        this.dataService.request('Project/get', {
            PID: this.PID()
        }).subscribe(response => {
            this.project = response.project;

            this.patchForm();
        });
    }

    patchForm(): void {
        this.form.patchValue({
            id: this.project.id,
            title_project: this.project.title_project,
            number: this.project.number,
            strategic_classification_root: this.project.strategic_classification ? strategicClassifications.find(sc => sc.subs.includes(this.project.strategic_classification)) : '',
            strategic_classification: this.project.strategic_classification ?? '',
        });

        this.formService.getFormArray(this.form, 'goals').clear();
        this.project.goals.forEach(item => {
            this.addFormItem(item, 'goals');
        });

        this.formService.getFormArray(this.form, 'non_goals').clear();
        this.project.non_goals.forEach(item => {
            this.addFormItem(item, 'non_goals');
        });

        this.formService.getFormArray(this.form, 'criteria').clear();
        this.project.criteria.forEach(item => {
            this.addFormItem(item, 'criteria');
        });
    }

    addFormItem(data: any = {}, control): void {
        const textWithBreaks = data.text ? data.text.replace(/\n/g, '<br>') : '';

        this.formService.getFormArray(this.form, control).push(
            this.fb.group({
                id: [data.id || null],
                id_project: [data.id_project || this.project.id],
                text: [data.text || '', Validators.required],
                textWithBreaks: [textWithBreaks],
                completed: [data.completed ? data.completed == '1' : null],
                _deleted: [false],
            })
        );
    }

    setMode(viewMode: ViewMode): void {
        this.viewMode = viewMode;
        if (this.viewMode == ViewMode.EDIT) {
            this.changeViewMode.emit(true);
        } else {
            this.changeViewMode.emit(false);
        }
    }

    update(): void {
        if (![ProjectStatus.REVISE].includes(this.project.status)) {
            this.dataService.request('Project/updateGoals', {
                project: this.form.getRawValue(),
            }).subscribe(() => {
                this.toastrService.success('Erfolgreich gespeichert')
                this.setMode(ViewMode.VIEW);
                this.getProject();
                this.changeProject.emit();
            });
        } else {
            Swal.fire({
                title: 'Sicher?',
                text: 'Der Status wird auf "' + this.projectStatusPipe.transform(ProjectStatus.IN_ELABORATION) + '" gesetzt.',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#DD6B55',
                confirmButtonText: 'Ja',
                cancelButtonText: 'Nein'
            }).then(result => {
                if (result.value) {
                    this.dataService.request('Project/setStatus', {
                        PID: this.PID(),
                        status: ProjectStatus.IN_ELABORATION,
                    }).subscribe(() => {
                        this.dataService.request('Project/updateGoals', {
                            project: this.form.getRawValue(),
                        }).subscribe(() => {
                            this.toastrService.success('Erfolgreich gespeichert')
                            this.setMode(ViewMode.VIEW);
                            this.getProject();
                            this.changeProject.emit();
                        });
                    });
                }
            });
        }
    }

    cancelEdit(): void {
        this.patchForm();
        this.setMode(ViewMode.VIEW);
    }

    markEntryAsDeleted(entry): void {
        entry.patchValue({
            _deleted: true,
        });
        for (const key in entry.controls) {
            entry.get(key).clearValidators();
            entry.get(key).updateValueAndValidity();
        }
    }

    getLengthOfFormArray(name): number {
        return this.formService.getFormArray(this.form, name).controls.filter(control => {
            return control.value._deleted == false;
        }).length;
    }

}
